import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';

import { HStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useChannelStore } from '../../../lib/channels/hooks/use-channel-store';
import { useCollaborativeDocument } from '../hooks/use-collaborative-document';
import { useUserColor } from '../hooks/use-user-color';
import { ActionCableProvider } from '../providers/action-cable-provider';
import { ConnectedUsers } from './connected-users';
import { ConnectionStatus } from './connection-status';
import { CoreEditor } from './core-editor';
import { LoadingState } from './loading-state';

interface CollaborativeEditorProps {
	// Temp prop since not all meetings have a collaborative document yet
	// but if this component is rendered, that means the user has the feature flag on
	// so its safe to call the endpoint on the meeting that either returns or creates the document
	meetingId: string;
	// documentId: string;
	channelName?: string;
	channelParams?: Record<string, any>;
	readOnly?: boolean;
	placeholder?: string;
	uploadEndpoint?: string;
	onEditorReady?: (editor: any) => void;
	onProviderReady?: (provider: ActionCableProvider) => void;
}

function CollaborativeEditorComponent({
	meetingId,
	channelName = 'CollaborativeDocumentChannel',
	channelParams,
	readOnly = false,
	placeholder,
	uploadEndpoint,
	onEditorReady,
	onProviderReady,
}: CollaborativeEditorProps) {
	const currentUser = useCurrentUser();
	const consumer = useChannelStore((state) => state.consumer);
	const userColor = useUserColor(currentUser.id, 'hex');

	// Fetch/create collaborative document for this meeting
	const { data: collaborativeDocument, isLoading: isLoadingDocument } = useCollaborativeDocument(meetingId);
	const documentId = collaborativeDocument?.id;

	// Create YJS document (one per document)
	// Note: documentId is intentionally in deps to recreate doc when document changes
	// Only create if we have a valid documentId
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const yjsDoc = useMemo(() => {
		if (!documentId) return null;
		return new Y.Doc();
	}, [documentId]);

	// Cleanup YJS document when component unmounts or documentId changes
	useEffect(() => {
		if (!yjsDoc) return;
		return () => {
			yjsDoc.destroy();
		};
	}, [yjsDoc, documentId]);

	const [provider, setProvider] = useState<ActionCableProvider | null>(null);

	// Store callback in ref to avoid effect re-runs when callback changes
	const onProviderReadyRef = useRef(onProviderReady);
	useEffect(() => {
		onProviderReadyRef.current = onProviderReady;
	}, [onProviderReady]);

	// Create ActionCable provider once we have the document ID and YJS doc
	useEffect(() => {
		if (!consumer || readOnly || !documentId || !yjsDoc) return;

		const newProvider = new ActionCableProvider(yjsDoc, {
			consumer,
			channel: channelName,
			documentId: documentId,
			channelParams: channelParams || {},
			user: {
				id: currentUser.id,
				name: currentUser.display_name,
				avatar: currentUser.profile_pic_url,
				color: userColor,
			},
			connect: true,
		});

		newProvider.on('synced', () => {
			setProvider(newProvider);
			onProviderReadyRef.current?.(newProvider);
		});

		return () => {
			newProvider.destroy();
			setProvider(null);
		};
	}, [consumer, documentId, channelName, channelParams, yjsDoc, currentUser.id, currentUser.display_name, currentUser.profile_pic_url, userColor, readOnly]);

	if (!consumer || !currentUser) {
		return <LoadingState message="Initializing..." />;
	}

	if (isLoadingDocument) {
		return <LoadingState message="Loading document..." />;
	}

	if (!documentId || !yjsDoc) {
		return <LoadingState message="Document not available" />;
	}

	if (!provider) {
		return <LoadingState message="Connecting..." />;
	}

	return (
		<>
			<HStack spacing={4} padding={2} width="100%" justifyContent="space-between">
				<ConnectionStatus provider={provider} showLabel />
				<ConnectedUsers provider={provider} maxVisible={5} />
			</HStack>
			<CoreEditor
				key={documentId}
				yjsDoc={yjsDoc}
				provider={provider}
				readOnly={readOnly}
				placeholder={placeholder}
				uploadEndpoint={uploadEndpoint}
				onEditorReady={onEditorReady}
			/>
		</>
	);
}

export const CollaborativeEditor = React.memo(CollaborativeEditorComponent);
